import React, { createContext, useContext, useState, ReactNode } from 'react';

interface NavigationContextType {
    currentSection: number;
    totalSections: number;
    goToNext: () => void;
    goToPrevious: () => void;
    goToSection: (index: number) => void;
    isFirstSection: boolean;
    isLastSection: boolean;
    sectionProgress: number;
    animationDirection: 'left' | 'right';
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigation = () => {
    const context = useContext(NavigationContext);
    if (!context) {
        throw new Error('useNavigation must be used within NavigationProvider');
    }
    return context;
};

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const totalSections = 10;
    const [currentSection, setCurrentSection] = useState(0);
    const [animationDirection, setAnimationDirection] = useState<'left' | 'right'>('left');

    const goToNext = () => {
        setAnimationDirection('left');
        setCurrentSection(prev => Math.min(prev + 1, totalSections - 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const goToPrevious = () => {
        setAnimationDirection('right');
        setCurrentSection(prev => Math.max(prev - 1, 0));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const goToSection = (index: number) => {
        const newDirection = index > currentSection ? 'left' : 'right';
        setAnimationDirection(newDirection);
        setCurrentSection(Math.max(0, Math.min(index, totalSections - 1)));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const isFirstSection = currentSection === 0;
    const isLastSection = currentSection === totalSections - 1;
    const sectionProgress = (currentSection + 1) / totalSections;

    return (
        <NavigationContext.Provider
            value={{
                currentSection,
                totalSections,
                goToNext,
                goToPrevious,
                goToSection,
                isFirstSection,
                isLastSection,
                sectionProgress,
                animationDirection,
            }}
        >
            {children}
        </NavigationContext.Provider>
    );
};
